import { io } from "socket.io-client";

class SocketService {
  constructor() {
    this.socket = null;
    this.connected = false;
    this.reconnecting = false;
    this.eventHandlers = new Map();
    this.reconnectAttempts = 0;
    this.connectionPromise = null; // Track connection promise
  }

  /**
   * Initialize socket connection
   * @param {string} baseUrl - Base URL for socket connection
   * @returns {Promise<boolean>} - Connection success status
   */
  async connect(baseUrl) {
    if (this.socket && this.connected) {
      console.log("✅ [SocketService] Already connected");
      return true;
    }

    // If connection is in progress, return the existing promise
    if (this.connectionPromise) {
      console.log("⏳ [SocketService] Connection already in progress, waiting...");
      return await this.connectionPromise;
    }

    try {
      const token = localStorage.getItem("accessToken");
      const socketUrl = baseUrl.replace(/\/$/, ""); // Remove trailing slash

      console.log("🔌 [SocketService] Connecting to:", socketUrl);

      this.socket = io(socketUrl, {
        auth: {
          token: token,
        },
        // Prefer websocket for stability
        transports: ["websocket", "polling"],
        // Keep connection alive with reasonable limits
        reconnection: true,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 10000,
        reconnectionAttempts: 10,
        timeout: 10000,
        // Force new connection
        forceNew: false,
        // Upgrade transport
        upgrade: true,
        // Reconnect automatically
        autoConnect: true,
      });

      // Setup event listeners
      this.setupEventListeners();

      // Wait for connection with timeout and track the promise
      this.connectionPromise = this.waitForConnection();
      const result = await this.connectionPromise;
      this.connectionPromise = null;
      return result;
    } catch (error) {
      console.error("❌ [SocketService] Connection error:", error);
      this.connected = false;
      this.connectionPromise = null;
      return false;
    }
  }

  /**
   * Wait for socket to be ready (either already connected or wait for connection)
   * @param {number} timeout - Maximum time to wait in milliseconds
   * @returns {Promise<boolean>} - Whether socket is ready
   */
  async waitForReady(timeout = 2000) {
    if (this.connected && this.socket && this.socket.connected) {
      return true;
    }

    // If connection is in progress, wait for it
    if (this.connectionPromise) {
      console.log("⏳ [SocketService] Waiting for connection to complete...");
      try {
        return await Promise.race([
          this.connectionPromise,
          new Promise((resolve) => setTimeout(() => resolve(false), timeout))
        ]);
      } catch {
        return false;
      }
    }

    return false;
  }

  /**
   * Wait for socket connection with timeout
   */
  waitForConnection(timeout = 5000) {
    return new Promise((resolve) => {
      if (!this.socket) {
        resolve(false);
        return;
      }

      const timer = setTimeout(() => {
        console.warn("⚠️ [SocketService] Connection timeout");
        resolve(false);
      }, timeout);

      const onConnect = () => {
        clearTimeout(timer);
        this.connected = true;
        this.reconnectAttempts = 0;
        console.log("✅ [SocketService] Connected successfully");
        resolve(true);
      };

      const onError = (error) => {
        clearTimeout(timer);
        console.error("❌ [SocketService] Connection failed:", error.message);
        resolve(false);
      };

      this.socket.once("connect", onConnect);
      this.socket.once("connect_error", onError);
    });
  }

  /**
   * Setup socket event listeners
   */
  setupEventListeners() {
    if (!this.socket) return;

    // Remove existing listeners to prevent duplicates
    this.socket.removeAllListeners();

    // Listen for server confirmation
    this.socket.on("connected", (data) => {
      console.log("✅ [SocketService] Server confirmed connection:", data);
      console.log(`   - Socket ID: ${data.socketId}`);
      console.log(`   - Restaurant: ${data.restaurantId}`);
      console.log(`   - Room: ${data.room}`);
    });

    this.socket.on("connect", () => {
      this.connected = true;
      this.reconnecting = false;
      this.reconnectAttempts = 0;
      console.log("✅ [SocketService] Connected - ID:", this.socket.id);
    });

    this.socket.on("disconnect", (reason) => {
      this.connected = false;
      console.warn("⚠️ [SocketService] Disconnected:", reason);

      // Always try to reconnect
      if (reason === "io server disconnect") {
        // Server disconnected, reconnect manually
        console.log("🔄 [SocketService] Server disconnected, reconnecting...");
        this.socket.connect();
      }
    });

    this.socket.on("connect_error", (error) => {
      this.connected = false;
      this.reconnectAttempts++;
      console.error(
        `❌ [SocketService] Connection error (attempt ${this.reconnectAttempts}/${this.socket.io.opts.reconnectionAttempts}):`,
        error.message
      );
    });

    this.socket.on("reconnect_attempt", (attemptNumber) => {
      this.reconnecting = true;
      console.log(`🔄 [SocketService] Reconnection attempt ${attemptNumber}`);
    });

    this.socket.on("reconnect", (attemptNumber) => {
      this.connected = true;
      this.reconnecting = false;
      this.reconnectAttempts = 0;
      console.log(
        `✅ [SocketService] Reconnected after ${attemptNumber} attempts`
      );
    });

    this.socket.on("reconnect_failed", () => {
      this.connected = false;
      this.reconnecting = false;
      console.error("❌ [SocketService] All reconnection attempts failed");
    });

    this.socket.on("error", (error) => {
      console.error("❌ [SocketService] Socket error:", error);
    });
  }

  /**
   * Emit event and wait for response
   * @param {string} event - Event name
   * @param {any} data - Data to send
   * @param {number} timeout - Timeout in milliseconds
   * @returns {Promise<any>} - Response data
   */
  async emit(event, data = {}, timeout = 10000) {
    if (!this.socket || !this.connected) {
      throw new Error("Socket not connected");
    }

    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        reject(new Error(`Socket emit timeout for event: ${event}`));
      }, timeout);

      console.log(`📤 [SocketService] Emitting event: ${event}`, data);

      this.socket.emit(event, data, (response) => {
        clearTimeout(timer);

        if (response && response.error) {
          console.error(
            `❌ [SocketService] Event ${event} error:`,
            response.error
          );
          reject(new Error(response.error));
        } else {
          console.log(`✅ [SocketService] Event ${event} response received`);
          resolve(response);
        }
      });
    });
  }

  /**
   * Listen to socket events
   * @param {string} event - Event name
   * @param {Function} callback - Callback function
   */
  on(event, callback) {
    if (!this.socket) {
      console.warn("⚠️ [SocketService] Socket not initialized");
      return;
    }

    console.log(`👂 [SocketService] Listening to event: ${event}`);

    // Store handler reference for cleanup
    if (!this.eventHandlers.has(event)) {
      this.eventHandlers.set(event, []);
    }
    this.eventHandlers.get(event).push(callback);

    this.socket.on(event, callback);
  }

  /**
   * Remove event listener
   * @param {string} event - Event name
   * @param {Function} callback - Callback function (optional)
   */
  off(event, callback) {
    if (!this.socket) return;

    if (callback) {
      this.socket.off(event, callback);

      // Remove from handlers map
      const handlers = this.eventHandlers.get(event);
      if (handlers) {
        const index = handlers.indexOf(callback);
        if (index > -1) {
          handlers.splice(index, 1);
        }
      }
    } else {
      this.socket.off(event);
      this.eventHandlers.delete(event);
    }

    console.log(`🔇 [SocketService] Stopped listening to event: ${event}`);
  }

  /**
   * Check if socket is connected
   * @returns {boolean}
   */
  isConnected() {
    return this.connected && this.socket && this.socket.connected;
  }

  /**
   * Disconnect socket
   */
  disconnect() {
    if (this.socket) {
      console.log("🔌 [SocketService] Disconnecting...");

      // Clean up all event listeners
      this.eventHandlers.forEach((handlers, event) => {
        handlers.forEach(handler => {
          this.socket.off(event, handler);
        });
      });
      this.eventHandlers.clear();

      this.socket.disconnect();
      this.socket = null;
      this.connected = false;
      this.reconnecting = false;
      this.reconnectAttempts = 0;
    }
  }

  /**
   * Get socket instance
   * @returns {Socket | null}
   */
  getSocket() {
    return this.socket;
  }
}

// Export singleton instance
export const socketService = new SocketService();
export default socketService;
